import type { AppState } from '../../store';
import { buildGraph, type GraphIndex } from '../../lib/graph';
import { buildHeatIndex, type HeatIndex } from '../../lib/health';
import { buildHiddenSets, canDocBtn, canEditMap, canEditTags, isPermHidden } from '../../lib/permissions';
import { isHidden as isHiddenPure } from '../../lib/visibility';
import { searchActive, type FilterState } from '../../lib/search';
import { canAssign } from '../../lib/responsibility';
import { tagDescendants } from '../../lib/tags';
import type { DocPerm, NodeId, TagId } from '../../lib/types';
import type { PresenceUser } from '../presence/PresenceStack';

/**
 * Everything derived that a map node needs, computed ONCE per store notification.
 *
 * THIS EXISTS FOR A MEASURABLE REASON. Each of 147 `MapNode`s subscribes with its own
 * selector, so every notification runs 147 selectors. Several of the things a node's
 * appearance depends on are not cheap to answer per node — the permission-hidden sets expand
 * hidden tags to their descendants, the tag filter expands the active tag to ITS descendants,
 * and the presence badge needs the whole roster grouped by node. Answering any of those
 * inside the per-node selector would make one keystroke O(nodes x tags).
 *
 * The cache key is the STATE OBJECT ITSELF. Zustand builds a fresh state object on every
 * `setState`, so a `WeakMap` keyed on it gives exactly one computation per notification and
 * cannot go stale: a changed store is, by construction, a different key. The entries are
 * dropped with the state object they belong to.
 */

export interface NodePresence {
  user: PresenceUser;
  editing: boolean;
}

export interface MapContext {
  graph: GraphIndex;
  /** `permHidden()` — hidden for this group, itself or under a hidden ancestor. */
  permHidden: (id: NodeId) => boolean;
  /** `isHidden()` — the above, or collapsed away. What decides whether a node renders. */
  isHidden: (id: NodeId) => boolean;
  /** Ids that render, in the map's own key order. */
  visibleIds: NodeId[];
  filter: FilterState;
  /** True when the search box or one of the three presence facets is doing anything. */
  searchOn: boolean;
  /**
   * Every tag the multi-tag filter reaches (each selected tag plus its sub-tags), mapped to
   * the SELECTED tag's colour — a node highlighted through a sub-tag still lights in the
   * colour of the tag the user clicked. First selection wins where selections overlap.
   */
  wantedTags: ReadonlyMap<TagId, string>;
  /** Tags a node may show a meta dot for: an invisible tag stays hidden outside edit mode. */
  showsInvisibleTags: boolean;
  /**
   * May this user restructure the map (V0.37: the `EDIT_MAP` brick) — what gates the drag,
   * Ctrl-add, delete and reparent in `MapStage`, and the delete cross on each node.
   */
  canEditMap: boolean;
  canDocBtn: (perm: DocPerm) => boolean;
  /** May the dotted PLANNED-but-missing pills show for this node (V0.42): only in edit
   *  or heatmap mode, and only for someone who could create the docs there (a responsible
   *  or an editor). A plain view-only reader never sees them, so the map view is unchanged. */
  showPlanned: (nodeId: NodeId) => boolean;
  /** Node id -> who else is sitting on it. Absent means nobody. */
  presence: Map<NodeId, NodePresence[]>;
  /**
   * The clearance heatmap's index (V0.39), built ONCE here — never per node in the selector.
   * Empty by construction while the mode is off or the health payload has not loaded, which
   * is what makes the mode-off paint byte-identical to a build that never heard of heat.
   */
  heat: HeatIndex;
}

const cache = new WeakMap<AppState, MapContext>();

export function mapContextOf(s: AppState): MapContext {
  const hit = cache.get(s);
  if (hit) return hit;

  const graph = buildGraph(s.edges);
  const hidden = buildHiddenSets(s.perm, s.tagEdges);
  const permHidden = (id: NodeId) => isPermHidden(id, s.edges, hidden);
  const isHidden = (id: NodeId) =>
    isHiddenPure(id, { nodes: s.nodes, edges: s.edges, hidden, forceOpen: s.forceOpen });

  const filter: FilterState = {
    nodes: s.nodes,
    edges: s.edges,
    tags: s.tags,
    tagEdges: s.tagEdges,
    searchQuery: s.searchQuery,
    searchScope: s.searchScope,
    searchDocKind: s.searchDocKind,
    contentHits: s.searchContentHits,
    activeTags: s.activeTags,
    activeDocStatus: s.activeDocStatus,
    chatHighlight: s.chatHighlight,
    isPermHidden: permHidden,
    editing: s.editing,
    // The historical field name; it feeds the invisible-tag reveal, EDIT_TAGS' since V0.37.
    canEdit: canEditTags(s.perm),
  };

  const wantedTags = new Map<TagId, string>();
  for (const sel of s.activeTags) {
    const color = s.tags[sel]?.color ?? '#5b8cff';
    for (const t of [sel, ...tagDescendants(s.tagEdges, sel)]) {
      if (!wantedTags.has(t)) wantedTags.set(t, color);
    }
  }

  const visibleIds: NodeId[] = [];
  for (const id in s.nodes) if (!isHidden(id)) visibleIds.push(id);

  // One entry per SOCKET, exactly as `rebuildPresence()` built it: somebody with two tabs on
  // the same node shows twice, and you are never badged on your own node.
  const presence = new Map<NodeId, NodePresence[]>();
  const myId = s.currentUser?.id;
  for (const u of s.presenceRoster) {
    if (!u || !u.loc || u.userId === myId) continue;
    const nodeId = u.loc.nodeId;
    if (!nodeId || !s.nodes[nodeId]) continue;
    const list = presence.get(nodeId);
    const entry: NodePresence = { user: u, editing: u.loc.mode === 'edit' };
    if (list) list.push(entry);
    else presence.set(nodeId, [entry]);
  }

  const ctx: MapContext = {
    graph,
    permHidden,
    isHidden,
    visibleIds,
    filter,
    searchOn: searchActive(filter),
    wantedTags,
    showsInvisibleTags: s.editing && canEditTags(s.perm),
    canEditMap: canEditMap(s.perm),
    canDocBtn: (perm) => canDocBtn(s.perm, perm),
    showPlanned: (nodeId) =>
      (s.editing || s.heatmapOn) && (canEditMap(s.perm) || canAssign(s, nodeId)),
    presence,
    heat: buildHeatIndex({
      on: s.heatmapOn,
      loaded: s.mapHealthLoaded,
      byNode: s.mapHealth,
      nodes: s.nodes,
      hidden: permHidden,
    }),
  };
  cache.set(s, ctx);
  return ctx;
}
